/**
 * What the Company sections share: the section table the side navigation and
 * the router both read, the section path builder, and two helpers used by
 * more than one section.
 *
 * Adding a section is one entry here plus one component under this folder.
 * The order here is the order in the navigation.
 *
 * A section that exists only because Fias granted something (`entitlement`)
 * is left out of the navigation for a Company without that grant — hidden
 * rather than shown empty, for the same reason the cards themselves render
 * nothing: an empty page advertises a capability the Company cannot have.
 */

import {
  Boxes,
  Globe,
  Layers,
  Mail,
  Palette,
  Receipt,
  Settings2,
  ShieldCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { CompanyEntitlementKey, CompanySettingsDto } from '@/lib/api';

export interface CompanySection {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Shown only to a Company holding this Fias grant. */
  entitlement?: CompanyEntitlementKey;
}

export const COMPANY_SECTIONS = [
  { id: 'general', label: 'General', icon: Settings2 },
  { id: 'people', label: 'People', icon: Users },
  { id: 'workspaces', label: 'Workspaces', icon: Layers },
  { id: 'approvals', label: 'Arche approvals', icon: ShieldCheck },
  // ADR-026: arches the Company owns — offers, access, release.
  { id: 'arches', label: 'Owned arches', icon: Boxes },
  { id: 'billing', label: 'Billing', icon: Receipt },
  { id: 'branding', label: 'Branding', icon: Palette, entitlement: 'company_branding' },
  { id: 'email', label: 'Email', icon: Mail, entitlement: 'company_email' },
  { id: 'web', label: 'Web', icon: Globe, entitlement: 'company_web' },
] as const satisfies readonly CompanySection[];

export type CompanySectionId = (typeof COMPANY_SECTIONS)[number]['id'];

export const DEFAULT_COMPANY_SECTION: CompanySectionId = 'general';

export function isCompanySectionId(value: string | undefined): value is CompanySectionId {
  return COMPANY_SECTIONS.some((s) => s.id === value);
}

/**
 * The sections this Company may open. While its grants are unknown (still
 * loading, or the load failed) only the ungated sections are offered, so a
 * gated item never flashes in and out.
 */
export function visibleCompanySections(
  entitlements: readonly CompanyEntitlementKey[] | undefined,
): Array<(typeof COMPANY_SECTIONS)[number]> {
  return COMPANY_SECTIONS.filter((s) => {
    const required = (s as CompanySection).entitlement;
    return !required || (entitlements ?? []).includes(required);
  });
}

export function companySectionPath(companyId: string, section: CompanySectionId): string {
  return `/company/${encodeURIComponent(companyId)}/${section}`;
}

export { describeCompanyError as describeError } from '../../lib/company-errors';

/** A person as the Owned arches screens name them: display name, else @username. */
export function personLabel(
  p: { username: string | null; displayName: string | null } | null,
): string {
  if (!p) return 'someone';
  return p.displayName ?? (p.username ? `@${p.username}` : 'someone');
}

export function memberLabel(m: CompanySettingsDto['members'][number]): string {
  return m.displayName ?? (m.username ? `@${m.username}` : `…${m.principalId.slice(-6)}`);
}
